import React from 'react';
import { styled } from '@linaria/react';
import { Reorder } from 'framer-motion';
import { Plus, X as XIcon } from 'lucide-react';

import { generateHSLString } from './GradientGenerator.helpers';
import ResponsiveColorPickerTooltip from './ResponsiveColorPickerTooltip';
import ClientOnly from '@/components/ClientOnly';

import type { Color, ColorWithId } from './GradientGenerator.helpers';

const dragSpring = {
  type: 'spring',
  stiffness: 270,
  damping: 30,
  restSpeed: 0.01,
  restDelta: 0.01,
};

export type ColorUnitType =
  | 'existing-color'
  | 'add-new-button'
  | 'empty';

interface Props {
  item: ColorWithId;
  type: ColorUnitType;
  isEditing: boolean;
  isReordering: boolean;
  canBeDeleted: boolean;
  hasMounted: boolean;
  prefersReducedMotion: boolean;
  onClick: () => void;
  onDismiss: () => void;
  onDelete: () => void;
  onDragStart: (event: any) => void;
  onDragEnd: () => void;
  setColor: (newColor: Color) => void;
}

function ColorUnit({
  item,
  type,
  isEditing,
  isReordering,
  canBeDeleted,
  hasMounted,
  prefersReducedMotion,
  onClick,
  onDismiss,
  onDelete,
  onDragStart,
  onDragEnd,
  setColor,
}: Props) {
  const { id, color } = item;

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // NOTE: This HTML structure is invalid, but I don't see an easy fix :/. The <li> should be directly inside the <ul>, but I need a wrapping element to contain the tooltip.
  return (
    <Wrapper
      ref={wrapperRef}
      data-should-fade-in={String(type === 'empty' && !!hasMounted)}
    >
      <ListItem
        key={id}
        value={item}
        transition={dragSpring}
        dragTransition={{
          restDelta: 0.001,
        }}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        drag={type === 'existing-color' ? 'x' : false}
        style={{
          '--active-cursor': isReordering ? 'grabbing' : 'pointer',
        }}
      >
        {type === 'empty' ? (
          <GrayBox />
        ) : (
          <PreviewWrapper
            onClick={onClick}
            style={{
              '--border-color':
                type === 'existing-color'
                  ? 'var(--color-gray-300)'
                  : 'var(--color-gray-100)',
              '--border-hover-color':
                type === 'existing-color'
                  ? 'var(--color-gray-700)'
                  : 'var(--color-gray-300)',
            }}
          >
            <Background
              key={`${id}-background`}
              style={{
                '--opacity': type === 'existing-color' ? 0 : 0.1,
              }}
            />
            <ColorBox
              key={`${id}-color-box`}
              style={{
                '--opacity': type === 'existing-color' ? 1 : 0,
                '--background': color
                  ? generateHSLString(color)
                  : 'white',
              }}
            />
            {type === 'add-new-button' && (
              <IconWrapper>
                <Plus size={32} />
              </IconWrapper>
            )}
          </PreviewWrapper>
        )}
        {type === 'existing-color' &&
          canBeDeleted &&
          !isEditing &&
          !isReordering && (
            <DeleteButton onClick={onDelete}>
              <XIcon />
            </DeleteButton>
          )}
      </ListItem>
      {color && (
        <ClientOnly>
          <ResponsiveColorPickerTooltip
            wrapperRef={wrapperRef}
            isVisible={isEditing}
            color={color}
            prefersReducedMotion={prefersReducedMotion}
            setColor={setColor}
            onDismiss={onDismiss}
          />
        </ClientOnly>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 48px;

  &[data-should-fade-in='true'] {
    animation: fadeIn 500ms 200ms both;
  }
`;

const ListItem = styled(Reorder.Item)`
  position: relative;
  z-index: var(--z-index);
  will-change: transform;
`;

const PreviewWrapper = styled.button`
  position: relative;
  z-index: 2;
  width: 100%;
  aspect-ratio: 1 / 1;
  /* Fallback height for legacy browsers */
  min-height: 60px;
  border-radius: 6px;
  border: 3px solid var(--border-color);
  background: var(--color-background);

  &:hover {
    border-color: var(--border-hover-color);
  }

  &:active {
    cursor: var(--active-cursor);
  }

  &:focus-visible {
    outline-offset: 6px;
    outline-color: white;
  }

  svg {
    display: block;
  }
`;

const GrayBox = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 4px;
  background: white;
  opacity: 0.05;
`;

const Background = styled.div`
  position: absolute;
  top: 3px;
  left: 3px;
  right: 3px;
  bottom: 3px;
  background: white;
  border-radius: 2px;
  opacity: var(--opacity);
  transition: opacity 250ms;
`;

const ColorBox = styled.div`
  position: absolute;
  top: 3px;
  left: 3px;
  right: 3px;
  bottom: 3px;
  width: calc(100% - 3px * 2);
  height: calc(100% - 3px * 2);
  border-radius: 2px;
  background: var(--background);
  box-shadow: var(--shadow-elevation-medium);
  opacity: var(--opacity);
  transition: opacity 400ms 100ms;
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 32px;
  height: 32px;
  margin: auto;
  color: white;

  svg {
    display: block;
  }
`;

const DeleteButton = styled.button`
  --size: 40px;
  position: absolute;
  z-index: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--size);
  height: var(--size);
  margin: 0 auto;
  opacity: 0;
  border-radius: 50%;
  transform: translateY(var(--size));
  transition:
    opacity 200ms,
    transform 300ms;
  color: white;
  animation: fadeIn 300ms;

  @media (prefers-reduced-motion: no-preference) {
    transform: translateY(0px);
  }

  ${ListItem}:hover & {
    opacity: 1;
    transform: translateY(var(--size));
    outline-offset: -2px;
  }

  ${PreviewWrapper}:focus-visible + &, &:focus-visible {
    opacity: 1;
    transform: translateY(var(--size));
    outline-offset: -2px;
    outline-color: white;
  }

  svg {
    display: block;
  }
`;

export default ColorUnit;
